import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import {
  AlertCircle,
  ChevronDown,
  ExternalLink,
  Loader2,
  Pencil,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import { Button } from "@/modern/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/modern/components/ui/collapsible";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { Site } from "@/bryntum-lib/models/Site";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { sitesApi } from "@/network/network.jsx";
import { cn } from "@/modern/utils";
import {
  applySiteFormData,
  mergeSiteApiIntoFormData,
  siteSchema,
  siteToFormData,
  type SiteFormData,
} from "@/react/schema/siteSchema";
import { useSite } from "../hooks/useSite";
import { confirmSlugRepublishIfNeeded } from "../hooks/useSlugRepublishConfirm";
import {
  AutoFillLink,
  Field,
  GO_LIVE_INPUT_CLASS,
  GO_LIVE_TEXTAREA_CLASS,
  GoLiveFormSection,
  GoLiveToggleRow,
  LivePreviewPanel,
  OgImageUploader,
  SiteEditorFooter,
  SiteEditorHeader,
  SiteEditorPreviewHeader,
} from "../components/shared/siteEditorUi";
import { isSiteLive, resolveSiteLiveUrl, resolveSitePreviewUrl, siteNeedsPersist } from "../utils";

interface SiteEditorProps {
  record: { siteRecord?: Site | null };
  onClose: () => void;
  editorInstance?: { className?: string; [key: string]: unknown };
}

const STATUS_CHIP_ID = "site-editor-status";

export const SiteEditor = ({ record, onClose, editorInstance }: SiteEditorProps) => {
  const { project, selectedBoard, userPreferences } = useSwivuBryntumContext();
  const boardSlug = (selectedBoard?.record as { slug?: string } | undefined)?.slug;

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record: prefRecord }) =>
      prefRecord.key === PERSISTENCE_STATE_KEYS.hasOpenAiApiKey,
  });

  const hasOpenAIKey = useMemo(() => {
    if (!userPreferences) return false;
    return userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";
  }, [userPreferences]);

  const {
    activeSite,
    isEnsuring,
    ensureError,
    syncSiteIfNeeded,
    isPublishing,
    isRepublishing,
    startPublish,
    includedPages,
    pagesMissingHtml,
    canPublish,
    publishBlockReason,
    siteReady,
    isReadOnly,
  } = useSite({ initialSite: record.siteRecord ?? null, syncOnMount: true });

  const [isGeneratingSeo, setIsGeneratingSeo] = useState(false);
  const autoFillSeoAttemptedRef = useRef(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  const isBusy = isPublishing || isRepublishing;

  const {
    control,
    reset,
    getValues,
    watch,
    trigger,
    formState: { errors, isDirty },
  } = useForm<SiteFormData>({
    resolver: zodResolver(siteSchema),
    defaultValues: activeSite ? siteToFormData(activeSite) : undefined,
  });

  useEffect(() => {
    if (activeSite) {
      reset(siteToFormData(activeSite));
    }
  }, [activeSite, reset]);

  const watchedSlug = watch("slug");
  const watchedMetaTitle = watch("metaTitle");
  const watchedMetaDescription = watch("metaDescription");
  const watchedOgTitle = watch("ogTitle");
  const watchedOgDescription = watch("ogDescription");
  const watchedOgImageUrl = watch("ogImageUrl");

  const handleOgImageUpdated = useCallback(() => {
    if (activeSite) {
      reset(siteToFormData(activeSite));
    }
  }, [activeSite, reset]);

  const persistBeforePublish = useCallback(async (): Promise<boolean> => {
    if (!activeSite || !project) return false;

    if (isDirty) {
      applySiteFormData(activeSite, getValues());
      try {
        await project.sync();
        reset(siteToFormData(activeSite));
      } catch {
        toast.error("Save your changes before publishing.");
        return false;
      }
    }

    return true;
  }, [activeSite, project, isDirty, getValues, reset]);

  const handleGenerateSeo = useCallback(async () => {
    if (!boardSlug || isReadOnly || isEnsuring) return;
    if (activeSite && siteNeedsPersist(activeSite)) {
      const synced = await syncSiteIfNeeded();
      if (!synced) {
        toast.error("Could not create site before generating SEO.");
        return;
      }
    }
    setIsGeneratingSeo(true);
    try {
      const res = await sitesApi(boardSlug).generateSeo();
      const apiSite = res?.data ?? {};
      const merged = mergeSiteApiIntoFormData(getValues(), apiSite);
      if (activeSite) {
        applySiteFormData(activeSite, merged);
        await project?.sync();
      }
      reset(merged);
      toast.success("SEO copy drafted from your board — review and tweak before publishing.");
    } catch {
      toast.error("Could not generate site SEO.");
    } finally {
      setIsGeneratingSeo(false);
    }
  }, [boardSlug, isReadOnly, isEnsuring, activeSite, syncSiteIfNeeded, getValues, project, reset]);

  useEffect(() => {
    if (autoFillSeoAttemptedRef.current) return;
    if (!siteReady || !activeSite || isReadOnly || isBusy || !hasOpenAIKey || !boardSlug) return;
    if (isSiteLive(activeSite)) return;

    const hasSeoContent = Boolean(
      activeSite.metaTitle?.trim() || activeSite.metaDescription?.trim(),
    );
    if (hasSeoContent) return;

    autoFillSeoAttemptedRef.current = true;
    void handleGenerateSeo();
  }, [siteReady, activeSite, isReadOnly, isBusy, hasOpenAIKey, boardSlug, handleGenerateSeo]);

  const handlePublish = async () => {
    if (isEnsuring) return;

    const valid = await trigger();
    if (!valid) {
      toast.error("Fix form errors before publishing.");
      return;
    }

    if (includedPages.length === 0) {
      toast.error("Turn on “Include in site” for at least one page first.");
      return;
    }
    if (pagesMissingHtml.length > 0) {
      toast.error(`${pagesMissingHtml.length} included page(s) still need generated HTML.`);
      return;
    }

    if (activeSite && siteNeedsPersist(activeSite)) {
      const synced = await syncSiteIfNeeded();
      if (!synced) {
        toast.error("Could not create site before publishing.");
        return;
      }
    }

    const live = isSiteLive(activeSite);
    const slugDirty = getValues("slug") !== (activeSite?.slug ?? "");
    if (live && slugDirty) {
      const confirmed = await confirmSlugRepublishIfNeeded(true, true);
      if (!confirmed) return;
    }

    const persisted = await persistBeforePublish();
    if (!persisted) return;

    await startPublish();
  };

  const publishedAt =
    activeSite?.publishedAt && !Number.isNaN(new Date(activeSite.publishedAt).getTime())
      ? format(new Date(activeSite.publishedAt), "MMM d, yyyy · h:mm a")
      : null;
  const isLive = isSiteLive(activeSite);
  const previewUrl = resolveSitePreviewUrl(activeSite, watchedSlug);
  const liveUrl = resolveSiteLiveUrl(activeSite);

  const previewProps = {
    title: watchedMetaTitle ?? "",
    url: previewUrl,
    description: watchedMetaDescription ?? "",
    socialTitle: watchedOgTitle || watchedMetaTitle || "",
    socialDescription: watchedOgDescription || watchedMetaDescription || "",
    imageUrl:
      activeSite?.ogImageThumbnailUrl?.trim() ||
      watchedOgImageUrl?.trim() ||
      activeSite?.ogImageUrl?.trim() ||
      "",
  };

  const publishSubtitle = isLive
    ? publishedAt
      ? `Last published · ${publishedAt}`
      : "Published"
    : "Not published yet";
  const headerTitle = isLive ? "Edit Site" : "Go live";
  const HeaderIcon = isLive ? Pencil : Upload;

  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <X className="size-5" />
    </button>
  );

  const showEnsuringBanner = isEnsuring;
  const showErrorBanner = Boolean(ensureError) && !isEnsuring;
  const showPublishingBanner = isPublishing || isRepublishing;
  const hasStatusBanners = showEnsuringBanner || showErrorBanner || showPublishingBanner;

  const statusBannerClass = "flex shrink-0 items-center gap-3 border-b px-6 py-2.5";

  const statusBanners = hasStatusBanners ? (
    <>
      {showEnsuringBanner && (
        <div className={cn(statusBannerClass, "border-border bg-muted/40")}>
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <p className="min-w-0 flex-1 text-sm text-foreground">Creating site for this board…</p>
        </div>
      )}
      {showErrorBanner && (
        <div className={cn(statusBannerClass, "border-destructive/30 bg-destructive/10")}>
          <p className="min-w-0 flex-1 text-sm text-destructive">{ensureError}</p>
          {!isReadOnly && (
            <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => void syncSiteIfNeeded()}>
              Retry
            </Button>
          )}
        </div>
      )}
      {showPublishingBanner && (
        <div className={cn(statusBannerClass, "border-primary/25 bg-primary/10")}>
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <p className="min-w-0 flex-1 text-sm text-foreground">
            <span className="font-medium">
              {isRepublishing ? "Republishing after slug change…" : "Publishing…"}
            </span>
            <span className="text-muted-foreground"> — you can close this window.</span>
          </p>
        </div>
      )}
    </>
  ) : null;

  const fieldDisabled = isBusy || isReadOnly;

  const headerActions = (
    <>
      {publishBlockReason && (
        <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
          <AlertCircle className="size-3.5 shrink-0" />
          {publishBlockReason}
        </span>
      )}
      {isLive && liveUrl && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={isBusy}
          onClick={() => window.open(liveUrl, "_blank", "noopener,noreferrer")}
        >
          View live site
          <ExternalLink className="size-4" aria-hidden />
        </Button>
      )}
      {!isReadOnly && (
        <Button
          type="button"
          size="sm"
          disabled={!canPublish}
          aria-describedby={!canPublish ? STATUS_CHIP_ID : undefined}
          className="gap-1.5"
          onClick={handlePublish}
        >
          {isPublishing ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Publishing…
            </>
          ) : (
            <>
              <Upload className="size-4" />
              Publish
            </>
          )}
        </Button>
      )}
      {closeButton}
    </>
  );

  return (
    <BaseModal
      record={activeSite ?? undefined}
      onClose={onClose}
      title=""
      description=""
      editorInstance={editorInstance}
      hideMenu
      modal
      className="relative !max-w-[96vw] flex flex-col resize overflow-hidden !gap-0 !p-0 !bg-background !text-foreground"
      size={{
        width: "min(75rem, 96vw)",
        height: "min(85vh, 90vh)",
        maxWidth: "96vw",
        maxHeight: "90vh",
        minWidth: "min(24rem, 96vw)",
        minHeight: "min(30rem, 90vh)",
      }}
      headerRenderer={() => null}
      footerRenderer={undefined}
    >
      <div className="drag-handle flex min-h-0 flex-1 flex-col overflow-hidden">
        {statusBanners}

        <SiteEditorHeader
          title={headerTitle}
          icon={HeaderIcon}
          isLive={isLive}
          isReadOnly={isReadOnly}
          trailing={headerActions}
        />

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 flex-1 overflow-hidden lg:flex-row">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-6 py-5">
              <div className="mb-6 flex shrink-0 justify-end">
                <AutoFillLink
                  onClick={() => void handleGenerateSeo()}
                  disabled={isReadOnly || isEnsuring || isBusy || Boolean(ensureError) || !hasOpenAIKey}
                  loading={isGeneratingSeo}
                  tooltip={
                    !hasOpenAIKey
                      ? "Add an OpenAI API key in Settings to auto-fill SEO from your board."
                      : undefined
                  }
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <div className="space-y-6">
                <GoLiveFormSection
                  title="Site address"
                  subtitle="The public URL path where visitors find your site."
                  helpText="The slug becomes part of your site URL. Use lowercase letters, numbers, and hyphens (e.g. smith-roofing-austin). Changing it after publish updates the live address and may require republishing."
                  first
                >
                  <Field label="Slug" required error={errors.slug?.message} variant="goLive">
                    <Controller
                      name="slug"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          variant="outline"
                          inputSize="sm"
                          disabled={fieldDisabled}
                          className={cn(GO_LIVE_INPUT_CLASS, "font-mono")}
                          autoComplete="off"
                        />
                      )}
                    />
                  </Field>
                </GoLiveFormSection>

                <GoLiveFormSection
                  title="Search appearance"
                  subtitle="How your homepage shows up in Google and other search results."
                  helpText="Page title: aim for about 60 characters — it's the blue headline in search results. Description: a short summary, ideally 150–160 characters. Allow indexing: when off, search engines are asked not to list this site."
                >
                  <Field label="Page title" variant="goLive">
                    <Controller
                      name="metaTitle"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          variant="outline"
                          inputSize="sm"
                          disabled={fieldDisabled}
                          className={GO_LIVE_INPUT_CLASS}
                          placeholder="Smith Roofing — Trusted local experts"
                        />
                      )}
                    />
                  </Field>

                  <Field label="Description" variant="goLive">
                    <Controller
                      name="metaDescription"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          disabled={fieldDisabled}
                          className={GO_LIVE_TEXTAREA_CLASS}
                          rows={3}
                          placeholder="Brief summary…"
                        />
                      )}
                    />
                  </Field>

                  <Controller
                    name="robotsIndex"
                    control={control}
                    render={({ field }) => (
                      <GoLiveToggleRow
                        id="site-robotsIndex"
                        label="Allow indexing"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={fieldDisabled}
                      />
                    )}
                  />
                </GoLiveFormSection>

                <GoLiveFormSection
                  title="Social sharing"
                  subtitle="How your site looks when someone pastes the link into social apps."
                  helpText="Upload a share image (recommended 1200×630 px). Share title and description override search appearance in link previews when filled in — leave blank to use those defaults."
                >
                  <Field label="Share image" variant="goLive">
                    {activeSite && (
                      <OgImageUploader
                        site={activeSite}
                        boardSlug={boardSlug}
                        disabled={fieldDisabled}
                        onUpdated={handleOgImageUpdated}
                      />
                    )}
                  </Field>

                  <Field label="Share title" variant="goLive">
                    <Controller
                      name="ogTitle"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          variant="outline"
                          inputSize="sm"
                          disabled={fieldDisabled}
                          className={GO_LIVE_INPUT_CLASS}
                          placeholder="Optional — uses page title when empty"
                        />
                      )}
                    />
                  </Field>

                  <Field label="Share description" variant="goLive">
                    <Controller
                      name="ogDescription"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          disabled={fieldDisabled}
                          className={GO_LIVE_TEXTAREA_CLASS}
                          rows={3}
                          placeholder="Optional — uses description when empty"
                        />
                      )}
                    />
                  </Field>
                </GoLiveFormSection>

                <Collapsible open={mobilePreviewOpen} onOpenChange={setMobilePreviewOpen} className="lg:hidden">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      aria-expanded={mobilePreviewOpen}
                      className="flex w-full items-center justify-between border-t border-border pt-6 text-sm font-medium text-muted-foreground"
                    >
                      Live preview
                      <ChevronDown
                        className={cn("size-4 transition-transform", mobilePreviewOpen && "rotate-180")}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-5">
                    <LivePreviewPanel {...previewProps} />
                  </CollapsibleContent>
                </Collapsible>

                <GoLiveFormSection
                  title="Custom domain"
                  subtitle="Tell search engines which URL is the official version of this site."
                  helpText="Optional. Set a canonical URL when your site lives at a custom domain so search engines know which address to treat as the primary one."
                >
                  <Field label="Canonical URL" variant="goLive">
                    <Controller
                      name="canonicalUrl"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          variant="outline"
                          inputSize="sm"
                          disabled={fieldDisabled}
                          className={GO_LIVE_INPUT_CLASS}
                          placeholder="https://"
                        />
                      )}
                    />
                  </Field>

                  {/* TODO: redo custom domain UI
                  {activeSite && (
                    <CustomDomainSection
                      site={activeSite}
                      boardSlug={boardSlug}
                      isReadOnly={isReadOnly}
                      onSiteUpdated={() => {
                        if (activeSite) reset(siteToFormData(activeSite));
                      }}
                    />
                  )}
                  */}

                  {/* TODO: redo site theme picker
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Site theme</Label>
                    <SiteThemeSection
                      value={themePreset}
                      onChange={setThemePreset}
                      disabled={fieldDisabled}
                      isLive={isLive}
                    />
                  </div>
                  */}

                </GoLiveFormSection>

                <GoLiveFormSection
                  title="Crawler rules"
                  subtitle="Fine-grained control over how search engines crawl your site."
                  helpText="Follow links: when off, crawlers are asked not to follow links on this site. Twitter card type: usually leave as summary_large_image for large image previews on X."
                >
                  <Controller
                    name="robotsFollow"
                    control={control}
                    render={({ field }) => (
                      <GoLiveToggleRow
                        id="site-robotsFollow"
                        label="Follow links"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={fieldDisabled}
                      />
                    )}
                  />

                  <Field label="Twitter card type" variant="goLive">
                    <Controller
                      name="twitterCard"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          variant="outline"
                          inputSize="sm"
                          disabled={fieldDisabled}
                          className={cn(GO_LIVE_INPUT_CLASS, "font-mono")}
                        />
                      )}
                    />
                  </Field>
                </GoLiveFormSection>
              </div>
              </div>
            </div>

            <div className="hidden min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-l border-border bg-muted/30 lg:flex">
              <SiteEditorPreviewHeader />
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6 pt-4">
                <LivePreviewPanel {...previewProps} fit />
              </div>
            </div>
          </div>

          <SiteEditorFooter
            subtitle={publishSubtitle}
            includedCount={includedPages.length}
            statusChipId={STATUS_CHIP_ID}
          />
        </div>
      </div>
    </BaseModal>
  );
};
